<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';
import {
  buildVisitEntryInformation,
  buildVisitHistoryItems,
  buildVisitHistorySummary,
} from 'dashboard/helper/lais/visitaPresenter';
import VisitsLoadMore from './VisitsLoadMore.vue';

const props = defineProps({
  visits: { type: Array, default: () => [] },
  visit: { type: Object, default: () => ({}) },
  ownerPresent: { type: Boolean, default: false },
  showVisitHistory: { type: Boolean, default: true },
  initialLimit: { type: Number, default: 3 },
  serverHasMore: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const summary = computed(() =>
  buildVisitHistorySummary(props.visits, {
    ownerPresent: props.ownerPresent,
    showVisitHistory: props.showVisitHistory,
  })
);
const historyItems = computed(() => buildVisitHistoryItems(props.visits));
const entryInformation = computed(() =>
  buildVisitEntryInformation(props.visit, {
    visits: props.visits,
    initialLimit: props.initialLimit,
  })
);
const expandedItems = ref(new Set());
const visibleHistoryCount = ref(props.initialLimit);
const displayedHistoryItems = computed(() =>
  historyItems.value.slice(0, visibleHistoryCount.value)
);
const hasLocalMoreHistory = computed(
  () => visibleHistoryCount.value < historyItems.value.length
);
const hasMoreHistory = computed(
  () => hasLocalMoreHistory.value || props.serverHasMore
);

const isExpanded = item => expandedItems.value.has(item.id);

const toggleDetails = item => {
  const next = new Set(expandedItems.value);
  if (next.has(item.id)) {
    next.delete(item.id);
  } else {
    next.add(item.id);
  }
  expandedItems.value = next;
};

const openHistoryMessage = item => {
  if (!item.whatsappUrl || typeof window === 'undefined') return;
  window.open(item.whatsappUrl, '_blank', 'noopener');
};

const showMoreHistory = () => {
  if (hasLocalMoreHistory.value) {
    visibleHistoryCount.value += props.initialLimit;
    return;
  }

  visibleHistoryCount.value += props.initialLimit;
  emit('loadMore');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if -->
  <section class="contents">
    <div
      v-if="entryInformation.visible"
      class="grid gap-4 rounded-lais border border-lais-border bg-white p-4"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p
            v-if="entryInformation.contacts.some(contact => contact.tag)"
            class="mb-2 inline-flex rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800"
          >
            Imóvel com consultor imobiliário
          </p>
          <h3 class="text-[16px] font-semibold text-lais-text">
            {{ entryInformation.title }}
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
            {{ entryInformation.helper }}
          </p>
        </div>
        <span
          class="i-lucide-door-open mt-0.5 size-5 shrink-0 text-lais-text-muted"
          aria-hidden="true"
        />
      </div>

      <dl
        v-if="entryInformation.rows.length"
        class="overflow-hidden rounded-lais-sm border border-lais-border"
      >
        <div
          v-for="row in entryInformation.rows"
          :key="row.label"
          class="grid gap-1 border-b border-lais-border px-3 py-2 last:border-b-0 sm:grid-cols-[160px_minmax(0,1fr)]"
        >
          <dt class="text-[11px] font-semibold uppercase text-lais-text-muted">
            {{ row.label }}
          </dt>
          <dd class="min-w-0 text-[13px] font-medium text-lais-text">
            {{ row.value }}
          </dd>
        </div>
      </dl>

      <div v-if="entryInformation.contacts.length" class="grid gap-2">
        <h4 class="text-[13px] font-semibold text-lais-text">
          Contatos para entrada
        </h4>
        <div class="grid gap-2 md:grid-cols-2">
          <article
            v-for="contact in entryInformation.contacts"
            :key="contact.key"
            class="grid gap-2 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-3"
          >
            <div class="min-w-0">
              <p class="truncate text-[13px] font-semibold text-lais-text">
                {{ contact.name }}
              </p>
              <p class="text-[12px] text-lais-text-muted">
                {{ contact.roleLabel }}
              </p>
              <p v-if="contact.phone" class="mt-1 text-[12px] text-lais-text">
                {{ contact.phone }}
              </p>
            </div>
            <a
              :href="contact.whatsappUrl || undefined"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex min-h-9 items-center justify-center gap-2 rounded-lais-sm px-3 text-[12px] font-semibold transition-colors"
              :class="
                contact.whatsappUrl
                  ? 'bg-lais-primary text-white hover:bg-lais-primary/90'
                  : 'pointer-events-none bg-white text-lais-text-muted'
              "
            >
              <span
                class="i-lucide-message-circle size-3.5"
                aria-hidden="true"
              />
              {{ contact.actionLabel }}
            </a>
          </article>
        </div>
      </div>
    </div>

    <div
      v-if="summary.visible"
      class="mt-4 rounded-lais border border-lais-border bg-white p-4"
    >
      <div class="mb-4 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-[16px] font-semibold text-lais-text">
            Histórico de informações de entrada das últimas visitas
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
            Consulte o que aconteceu nas últimas visitas antes de confirmar a
            entrada no imóvel.
          </p>
        </div>
        <span
          class="i-lucide-history mt-0.5 size-5 shrink-0 text-lais-text-muted"
          aria-hidden="true"
        />
      </div>

      <div class="flex items-start gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-full"
          :class="summary.tone"
        >
          <span :class="summary.icon" class="size-5" aria-hidden="true" />
        </span>
        <div class="min-w-0">
          <h3 class="text-[16px] font-semibold text-lais-text">
            {{ summary.title }}
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
            {{ summary.description }}
          </p>
        </div>
      </div>

      <dl class="mt-4 grid grid-cols-3 gap-2 text-center">
        <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
          <dt class="text-[11px] font-semibold uppercase text-lais-text-muted">
            Total
          </dt>
          <dd class="mt-1 text-[20px] font-semibold text-lais-text">
            {{ summary.total }}
          </dd>
        </div>
        <div class="rounded-lais-sm bg-emerald-50 px-3 py-2">
          <dt class="text-[11px] font-semibold uppercase text-emerald-700">
            {{ summary.finishedLabel }}
          </dt>
          <dd class="mt-1 text-[20px] font-semibold text-emerald-700">
            {{ summary.finished }}
          </dd>
        </div>
        <div class="rounded-lais-sm bg-rose-50 px-3 py-2">
          <dt class="text-[11px] font-semibold uppercase text-rose-700">
            {{ summary.unfinishedLabel }}
          </dt>
          <dd class="mt-1 text-[20px] font-semibold text-rose-700">
            {{ summary.unfinished }}
          </dd>
        </div>
      </dl>

      <div v-if="historyItems.length" class="mt-4 grid gap-3">
        <article
          v-for="item in displayedHistoryItems"
          :key="item.id"
          class="rounded-lais-sm border bg-white p-3"
          :class="item.tone"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-white/70"
            >
              <span :class="item.icon" class="size-4" aria-hidden="true" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-bold"
                >
                  {{ item.typeLabel }}
                </span>
                <p class="text-[13px] font-semibold">
                  {{ item.title }}
                </p>
              </div>
              <p class="mt-1 text-[12px] leading-relaxed">
                {{ item.subtitle }}
              </p>
              <p class="mt-1 text-[11px] font-semibold opacity-80">
                {{ item.scheduledAtLabel }}
              </p>

              <button
                v-if="item.details.length || item.actions.length"
                type="button"
                class="mt-3 inline-flex items-center gap-1.5 rounded-full border border-current bg-white/80 px-3 py-1.5 text-[12px] font-semibold"
                :aria-expanded="isExpanded(item)"
                @click="toggleDetails(item)"
              >
                <span
                  :class="
                    isExpanded(item)
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  class="size-4"
                  aria-hidden="true"
                />
                {{
                  isExpanded(item) ? 'Esconder detalhes' : 'Mostrar detalhes'
                }}
              </button>

              <div
                v-if="isExpanded(item)"
                class="mt-3 overflow-hidden rounded-lais-sm border border-white/70 bg-white/80"
              >
                <dl
                  v-if="item.details.length"
                  class="divide-y divide-lais-border"
                >
                  <div
                    v-for="detail in item.details"
                    :key="`${item.id}-${detail.label}`"
                    class="grid gap-1 px-3 py-2 sm:grid-cols-[150px_minmax(0,1fr)]"
                  >
                    <dt class="text-[11px] font-semibold uppercase opacity-70">
                      {{ detail.label }}
                    </dt>
                    <dd class="min-w-0 text-[13px] font-medium">
                      {{ detail.value }}
                    </dd>
                  </div>
                </dl>

                <div
                  v-if="item.whatsappUrl"
                  class="border-t border-lais-border px-3 py-2"
                >
                  <button
                    type="button"
                    class="inline-flex w-full items-center justify-center gap-2 rounded-lais-sm px-3 py-2 text-[13px] font-semibold hover:bg-lais-bg-soft"
                    @click="openHistoryMessage(item)"
                  >
                    <span
                      class="i-lucide-message-circle size-4"
                      aria-hidden="true"
                    />
                    Falar com o corretor da visita
                  </button>
                </div>
              </div>

              <button
                v-else-if="item.whatsappUrl"
                type="button"
                class="mt-3 inline-flex items-center gap-1.5 rounded-full border border-current bg-white/80 px-3 py-1.5 text-[12px] font-semibold"
                @click="openHistoryMessage(item)"
              >
                <span
                  class="i-lucide-message-circle size-4"
                  aria-hidden="true"
                />
                Falar com o corretor da visita
              </button>
            </div>
          </div>
        </article>

        <div v-if="hasMoreHistory" class="flex justify-center">
          <VisitsLoadMore
            :loading="loadingMore"
            :label="
              loadingMore ? 'Carregando...' : entryInformation.loadMoreLabel
            "
            @click="showMoreHistory"
          />
        </div>
      </div>
    </div>
  </section>
</template>
